import type { ReportService } from "../services/reportService";

export interface DateRange {
  from: Date;
  to: Date;
}

export interface ReportColumn {
  field: string;
  header: string;
  // CSS width, e.g. '200px'. Without a width the browser sizes the column.
  width?: string;
  align?: "start" | "end";
  // 'currency' formats numbers as EUR, 'number' formats them with two decimals, 'link' renders the value as a link labelled "Link", 'html' renders the server markup as HTML.
  format?: "currency" | "number" | "link" | "html";
}

export interface ReportView {
  columns: ReportColumn[];
  // Rows are sorted ascending by this field and a header row is shown per group.
  groupBy?: string;
}

export interface PreparedReport {
  view: ReportView;
  rows: Record<string, unknown>[];
}

export interface ReportDefinition {
  key: string;
  label: string;
  requiresDates: boolean;
  // Shows a Mitarbeiter picker. The selected personalID is passed to fetch.
  requiresMitarbeiter?: boolean;
  // Shows a Meldedatum picker (April 15 of a year). The selected date is passed to fetch.
  requiresMeldeDatum?: boolean;
  // Reports without a matching service method have no fetch and show the empty message.
  fetch?: (
    service: ReportService,
    range: DateRange,
    mitarbeiterId: number | null,
    meldeDatum: Date | null,
  ) => Promise<unknown[]>;
  // Without a view, all keys of the fetched data are shown as columns.
  view?: (today: Date) => ReportView;
  // For reports whose columns depend on the data. Replaces view and rows.
  prepare?: (rows: Record<string, unknown>[], today: Date) => PreparedReport;
}

interface Semester {
  isSummer: boolean;
  startYear: number;
}

const SUMMER_SEMESTER_START_MONTH = 2;
const WINTER_SEMESTER_START_MONTH = 9;

const getSemester = (date: Date): Semester => {
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  if (
    month >= SUMMER_SEMESTER_START_MONTH &&
    month < WINTER_SEMESTER_START_MONTH
  ) {
    return { isSummer: true, startYear: year };
  }
  return {
    isSummer: false,
    startYear: month < SUMMER_SEMESTER_START_MONTH ? year - 1 : year,
  };
};

const shortYear = (year: number): string => String(year).slice(-2);

const formatSemester = ({ isSummer, startYear }: Semester): string =>
  isSummer
    ? `SS${shortYear(startYear)}`
    : `WS${shortYear(startYear)}/${shortYear(startYear + 1)}`;

const getNextSemester = ({ isSummer, startYear }: Semester): Semester =>
  isSummer
    ? { isSummer: false, startYear }
    : { isSummer: true, startYear: startYear + 1 };

const semesterOrder = ({ isSummer, startYear }: Semester): number =>
  startYear * 2 + (isSummer ? 0 : 1);

// Parses labels like "SS25" or "WS25/26". Only the start year is needed for ordering.
const parseSemester = (label: string): Semester => ({
  isSummer: label.startsWith("SS"),
  startYear: 2000 + Number(label.slice(2, 4)),
});

const SOLL_IST_BASE_COLUMNS: ReportColumn[] = [
  { field: "persNr", header: "PersonalNummer", width: "120px" },
  { field: "nachname", header: "Nachname", width: "150px" },
  { field: "vorname", header: "Vorname", width: "150px" },
  { field: "projektId", header: "Projekt-ID", width: "100px" },
  { field: "projekttitel", header: "Projekttitel", width: "320px" },
  {
    field: "kostentraegernummer",
    header: "Kostenträgernummer",
    width: "140px",
  },
];

// One Soll and one Ist column per semester up to the current one, like the old grid.
const prepareSollIstStunden = (
  rows: Record<string, unknown>[],
  today: Date,
): PreparedReport => {
  const currentSemester = getSemester(today);
  const currentOrder = semesterOrder(currentSemester);
  const currentLabel = formatSemester(currentSemester);

  const semesterLabels = new Set<string>();
  rows.forEach((row) => {
    Object.keys((row.swsReduktionenSemester ?? {}) as object).forEach((s) =>
      semesterLabels.add(s),
    );
    Object.keys((row.swsReduktionenSemesterFHelix ?? {}) as object).forEach(
      (s) => semesterLabels.add(s),
    );
  });
  const semesters = [...semesterLabels]
    .filter((label) => semesterOrder(parseSemester(label)) <= currentOrder)
    .sort(
      (a, b) =>
        semesterOrder(parseSemester(a)) - semesterOrder(parseSemester(b)),
    );

  const semesterColumns: ReportColumn[] = semesters.flatMap((label) => {
    const isCurrent = label === currentLabel;
    return [
      {
        field: `soll_${label}`,
        header: isCurrent ? "Aktuelles Semester Soll" : `${label} Soll`,
        width: "110px",
        align: "end" as const,
      },
      {
        field: `ist_${label}`,
        header: isCurrent ? "Aktuelles Semester Ist" : `${label} Ist`,
        width: "110px",
        align: "end" as const,
      },
    ];
  });

  const roundedValue = (dictionary: unknown, label: string): number => {
    const value =
      (dictionary as Record<string, number> | undefined)?.[label] ?? 0;
    return Math.round(value * 10) / 10;
  };

  const preparedRows = rows.map((row) => {
    const prepared: Record<string, unknown> = { ...row };
    semesters.forEach((label) => {
      prepared[`soll_${label}`] = roundedValue(
        row.swsReduktionenSemesterFHelix,
        label,
      );
      prepared[`ist_${label}`] = roundedValue(
        row.swsReduktionenSemester,
        label,
      );
    });
    return prepared;
  });

  return {
    view: {
      columns: [...SOLL_IST_BASE_COLUMNS, ...semesterColumns],
      groupBy: "fullName",
    },
    rows: preparedRows,
  };
};

const STUNDENREDUKTIONEN_BASE_COLUMNS: ReportColumn[] = [
  { field: "fullName", header: "Name", width: "180px" },
  { field: "projektId", header: "Projekt-ID", width: "100px" },
  { field: "kostentraegernummer", header: "KTR", width: "120px" },
  { field: "departmentDepartment", header: "Department", width: "160px" },
  { field: "department", header: "Institut", width: "160px" },
  { field: "projekttitel", header: "Projektname", width: "280px" },
  { field: "projektstatus", header: "Projektstatus", width: "130px" },
  {
    field: "storniertArchiviertAsText",
    header: "Storniert/Archiviert",
    width: "150px",
  },
];

// One column per semester found in the data, in chronological order.
const prepareStundenreduktionen = (
  rows: Record<string, unknown>[],
): PreparedReport => {
  const semesterLabels = new Set<string>();
  rows.forEach((row) => {
    Object.keys((row.swsReduktionenSemester ?? {}) as object).forEach((s) =>
      semesterLabels.add(s),
    );
  });
  const semesters = [...semesterLabels].sort(
    (a, b) => semesterOrder(parseSemester(a)) - semesterOrder(parseSemester(b)),
  );

  const semesterColumns: ReportColumn[] = semesters.map((label) => ({
    field: `ist_${label}`,
    header: label,
    width: "90px",
    align: "end" as const,
  }));

  const roundedValue = (dictionary: unknown, label: string): number => {
    const value =
      (dictionary as Record<string, number> | undefined)?.[label] ?? 0;
    return Math.round(value * 10) / 10;
  };

  const preparedRows = rows.map((row) => {
    const prepared: Record<string, unknown> = { ...row };
    semesters.forEach((label) => {
      prepared[`ist_${label}`] = roundedValue(
        row.swsReduktionenSemester,
        label,
      );
    });
    prepared.swsReduktionenGesamt =
      Math.round(Number(row.swsReduktionenGesamt ?? 0) * 10) / 10;
    return prepared;
  });

  return {
    view: {
      columns: [
        ...STUNDENREDUKTIONEN_BASE_COLUMNS,
        {
          field: "swsReduktionenGesamt",
          header: "Gesamt",
          width: "90px",
          align: "end",
        },
        ...semesterColumns,
      ],
      groupBy: "fullName",
    },
    rows: preparedRows,
  };
};

const STUNDENREDUKTIONEN_VERGLEICH_BASE_COLUMNS: ReportColumn[] = [
  { field: "persNr", header: "PersonalNummer", width: "120px" },
  { field: "nachname", header: "Nachname", width: "150px" },
  { field: "vorname", header: "Vorname", width: "150px" },
  { field: "projektId", header: "Projekt-ID", width: "100px" },
  { field: "projekttitel", header: "Projekttitel", width: "320px" },
  {
    field: "storniertArchiviertAsText",
    header: "Storniert/Archiviert",
    width: "150px",
  },
];

// One FHelix and one FHprojects column per semester up to the current one.
const prepareStundenreduktionenVergleich = (
  rows: Record<string, unknown>[],
  today: Date,
): PreparedReport => {
  const currentSemester = getSemester(today);
  const currentOrder = semesterOrder(currentSemester);
  const currentLabel = formatSemester(currentSemester);

  const semesterLabels = new Set<string>();
  rows.forEach((row) => {
    Object.keys((row.swsReduktionenSemester ?? {}) as object).forEach((s) =>
      semesterLabels.add(s),
    );
    Object.keys((row.swsReduktionenSemesterFHelix ?? {}) as object).forEach(
      (s) => semesterLabels.add(s),
    );
  });
  const semesters = [...semesterLabels]
    .filter((label) => semesterOrder(parseSemester(label)) <= currentOrder)
    .sort(
      (a, b) =>
        semesterOrder(parseSemester(a)) - semesterOrder(parseSemester(b)),
    );

  const semesterColumns: ReportColumn[] = semesters.flatMap((label) => {
    const isCurrent = label === currentLabel;
    return [
      {
        field: `fhelix_${label}`,
        header: isCurrent ? "Aktuelles Semester FHelix" : `${label} FHelix`,
        width: "120px",
        align: "end" as const,
      },
      {
        field: `fhprojects_${label}`,
        header: isCurrent
          ? "Aktuelles Semester FHprojects"
          : `${label} FHprojects`,
        width: "140px",
        align: "end" as const,
      },
    ];
  });

  const roundedValue = (dictionary: unknown, label: string): number => {
    const value =
      (dictionary as Record<string, number> | undefined)?.[label] ?? 0;
    return Math.round(value * 10) / 10;
  };

  const preparedRows = rows.map((row) => {
    const prepared: Record<string, unknown> = { ...row };
    semesters.forEach((label) => {
      prepared[`fhelix_${label}`] = roundedValue(
        row.swsReduktionenSemesterFHelix,
        label,
      );
      prepared[`fhprojects_${label}`] = roundedValue(
        row.swsReduktionenSemester,
        label,
      );
    });
    return prepared;
  });

  return {
    view: {
      columns: [
        ...STUNDENREDUKTIONEN_VERGLEICH_BASE_COLUMNS,
        ...semesterColumns,
      ],
      groupBy: "fullName",
    },
    rows: preparedRows,
  };
};

const buildMeineProjektplanstundenView = (today: Date): ReportView => {
  let semester = getSemester(today);
  const semesterColumns: ReportColumn[] = Array.from({ length: 11 }, (_, i) => {
    if (i > 0) semester = getNextSemester(semester);
    return {
      field: `sollstundenSemester${i + 1}`,
      header: formatSemester(semester),
      width: "50px",
    };
  });

  return {
    groupBy: "fullName",
    columns: [
      { field: "fullName", header: "Name", width: "200px" },
      {
        field: "anstellung",
        header: "Anstellung in h (WStd * 43)",
        width: "60px",
      },
      { field: "projektId", header: "Projekt-ID", width: "60px" },
      { field: "kostentraegernummer", header: "KTR", width: "60px" },
      { field: "departmentDepartment", header: "Department", width: "80px" },
      { field: "department", header: "Institut", width: "80px" },
      { field: "projekttitel", header: "Projektname", width: "150px" },
      { field: "projektstatus", header: "Projektstatus", width: "100px" },
      { field: "iststunden", header: "Iststunden gesamt", width: "60px" },
      { field: "sollstunden", header: "Sollstunden gesamt", width: "60px" },
      ...semesterColumns,
    ],
  };
};

const buildProjektpartnerView = (): ReportView => ({
  columns: [
    {
      field: "partnerBezeichnung",
      header: "Firma/Institution",
      width: "260px",
    },
    { field: "plzOrt", header: "Ort", width: "160px" },
    { field: "nation", header: "Nation", width: "110px" },
    {
      field: "finanzPartnerCodeBezeichnung",
      header: "Finanzierungspartnercode",
      width: "180px",
    },
    {
      field: "koopPartnerCodeBezeichnung",
      header: "Kooperationspartnercode",
      width: "180px",
    },
    { field: "isAuftraggeberAsText", header: "Auftraggeber", width: "80px" },
    { field: "projektrolle", header: "Projektrolle", width: "80px" },
    { field: "projektId", header: "ProjektID", width: "60px" },
    { field: "kostentraegernummer", header: "KTR", width: "60px" },
    { field: "projekttitel", header: "Projekttitel", width: "320px" },
    { field: "projektstatus", header: "Projektstatus", width: "160px" },
    {
      field: "projektvolumenIMC",
      header: "Projektvolumen IMC",
      width: "150px",
      align: "end",
      format: "currency",
    },
    {
      field: "urlToProjectData",
      header: "Link",
      width: "70px",
      format: "link",
    },
  ],
});

const buildProjektpartnerProProjektView = (): ReportView => ({
  columns: [
    { field: "projektId", header: "ProjektID", width: "60px" },
    { field: "kostentraegernummer", header: "KTR", width: "60px" },
    { field: "projekttitel", header: "Projekttitel", width: "200px" },
    { field: "projektstatus", header: "Projektstatus", width: "160px" },
    { field: "projektart", header: "Projektart", width: "140px" },
    { field: "projektleiter", header: "Projektleiter", width: "160px" },
    { field: "departmentDepartment", header: "Department", width: "140px" },
    { field: "department", header: "Institut/Abteilung", width: "180px" },
    {
      field: "urlToProjectData",
      header: "Link",
      width: "70px",
      format: "link",
    },
    {
      field: "partnerBezeichnung",
      header: "Firma/Institution",
      width: "260px",
    },
    { field: "plzOrt", header: "Ort", width: "160px" },
    { field: "nation", header: "Nation", width: "110px" },
    { field: "isAuftraggeberAsText", header: "Auftraggeber", width: "80px" },
    { field: "projektrolle", header: "Projektrolle", width: "80px" },
    { field: "isAktivAsText", header: "Aktiv", width: "50px" },
  ],
});

const buildZuStellendeRechnungenView = (): ReportView => ({
  columns: [
    { field: "kostentraegernummer", header: "KTR", width: "60px" },
    { field: "stammKostentraegernummer", header: "StammKTR", width: "80px" },
    { field: "projekttitel", header: "Projekttitel", width: "320px" },
    { field: "projektstatus", header: "Projektstatus", width: "160px" },
    { field: "projektleiter", header: "Projektleiter", width: "160px" },
    { field: "projektart", header: "Projektart", width: "140px" },
    { field: "zahlungsplandatum", header: "Zahlungsplandatum", width: "150px" },
    { field: "urlToProjectData", header: "", width: "70px", format: "link" },
  ],
});

const buildOffeneZahlungslisteView = (): ReportView => ({
  columns: [
    { field: "kostentraegernummer", header: "KTR", width: "60px" },
    { field: "stammKostentraegernummer", header: "StammKTR", width: "60px" },
    { field: "projekttitel", header: "Projekttitel", width: "220px" },
    { field: "reNummer", header: "ReNummer", width: "70px" },
    { field: "reDatum", header: "ReDatum", width: "70px" },
    { field: "empfaenger", header: "Rechnungsempfänger", width: "220px" },
    {
      field: "betragNetto",
      header: "Rechnungsbetrag (Netto)",
      width: "170px",
      align: "end",
      format: "currency",
    },
    {
      field: "betragNettoOffen",
      header: "Offener Betrag (Netto)",
      width: "170px",
      align: "end",
      format: "currency",
    },
    { field: "anzahlMahnungen", header: "Anzahl Mahnungen", width: "100px" },
    { field: "bemerkungen", header: "Bemerkungen in Rechnung", width: "240px" },
    {
      field: "urlToProjectData",
      header: "Link",
      width: "70px",
      format: "link",
    },
  ],
});

const buildParalleleProjekteView = (): ReportView => ({
  columns: [
    { field: "mitarbeiterName", header: "Mitarbeiter", width: "180px" },
    { field: "projekttitelIntern", header: "Projekttitel", width: "320px" },
    { field: "kostentraegerNummer", header: "KTR", width: "60px" },
    { field: "projektStatusAsText", header: "ProjektStatus", width: "160px" },
    { field: "projektstartAsText", header: "Projektstart", width: "70px" },
    { field: "projektendeAsText", header: "Projektende", width: "70px" },
    { field: "stunden", header: "Stunden", width: "90px", align: "end" },
  ],
});

const buildProjektleiterBmdView = (): ReportView => ({
  columns: [
    {
      field: "kostentraegernummer",
      header: "Kostenträgernummer",
      width: "140px",
    },
    { field: "projekttitel", header: "Projekttitel", width: "340px" },
    {
      field: "projektleiter",
      header: "Projektleiter aus Projekt",
      width: "200px",
    },
    {
      field: "projektleiterBMD",
      header: "Projektleiter aus BMD",
      width: "200px",
    },
    {
      field: "hasProjektleiterDifferenzAsText",
      header: "Unterschied",
      width: "120px",
    },
    { field: "urlToProjectData", header: "", width: "70px", format: "link" },
  ],
});

const buildProjektmitarbeiterFindenView = (): ReportView => ({
  columns: [
    { field: "fullName", header: "Mitarbeiter", width: "180px" },
    {
      field: "kostentraegernummer",
      header: "Kostenträgernummer",
      width: "140px",
    },
    {
      field: "stammKostentraegernummer",
      header: "Stammkostenträgernummer",
      width: "170px",
    },
    { field: "projekttitel", header: "Projekttitel", width: "300px" },
    { field: "projektstatus", header: "Projektstatus", width: "140px" },
    { field: "storniertArchiviertAsText", header: "Storniert", width: "140px" },
    { field: "rolleAsText", header: "Projektrolle", width: "140px" },
    { field: "sollstunden", header: "Sollstunden", width: "110px" },
    { field: "iststunden", header: "Iststunden", width: "110px" },
    {
      field: "differenzSollIst",
      header: "Differenz",
      width: "110px",
      align: "end",
    },
    { field: "aktivAsText", header: "Aktiv", width: "90px", align: "end" },
    { field: "urlToProjectData", header: "", width: "70px", format: "link" },
  ],
});

const buildProjektmitarbeiterMitProjektenView = (): ReportView => ({
  columns: [
    { field: "projektId", header: "ID", width: "80px" },
    {
      field: "kostentraegernummer",
      header: "Kostenträgernummer",
      width: "140px",
    },
    { field: "projekttitel", header: "Projekttitel", width: "300px" },
    { field: "projektstatus", header: "Projektstatus", width: "140px" },
    {
      field: "storniertArchiviertAsText",
      header: "Storniert/Archiviert",
      width: "150px",
    },
    { field: "projektart", header: "Projektart", width: "140px" },
    { field: "department", header: "Institut/Abteilung", width: "180px" },
    { field: "projektbeginn", header: "Operativer Beginn", width: "120px" },
    { field: "projektende", header: "Operatives Ende", width: "120px" },
    { field: "fullName", header: "Mitarbeiter", width: "180px" },
    { field: "rolleAsText", header: "Projektrolle", width: "140px" },
    { field: "sollstunden", header: "Sollstunden", width: "110px" },
    { field: "iststunden", header: "Iststunden", width: "110px" },
    {
      field: "differenzSollIst",
      header: "Differenz",
      width: "110px",
      align: "end",
    },
    { field: "aktivAsText", header: "Aktiv", width: "90px", align: "end" },
    { field: "lohnkosten", header: "Lohnkosten", width: "120px", align: "end" },
    { field: "urlToProjectData", header: "", width: "70px", format: "link" },
  ],
});

const buildBerichteView = (): ReportView => ({
  columns: [
    {
      field: "kostentraegernummer",
      header: "Kostenträgernummer",
      width: "140px",
    },
    { field: "projekttitel", header: "Projekttitel", width: "300px" },
    { field: "projektStatus", header: "ProjektStatus", width: "140px" },
    { field: "projektleiter", header: "Projektleiter", width: "160px" },
    { field: "prefixTitle", header: "Berichtsnummer", width: "130px" },
    { field: "bezeichnung", header: "Bezeichnung", width: "220px" },
    { field: "datumVonAsText", header: "Zeitraum Von", width: "110px" },
    { field: "datumBisAsText", header: "Zeitraum Bis", width: "110px" },
    {
      field: "datumFaelligkeitAsText",
      header: "Fälligkeitsdatum",
      width: "130px",
    },
    {
      field: "isZwischenevaluierungAsText",
      header: "Zwischenevaluierung",
      width: "150px",
    },
    { field: "isEingereichtAsText", header: "Eingereicht", width: "100px" },
    { field: "isAngenommenAsText", header: "Angenommen", width: "100px" },
    { field: "urlToProjectData", header: "", width: "70px", format: "link" },
  ],
});

const buildZeitaufzeichnungView = (): ReportView => ({
  columns: [
    { field: "projektId", header: "ProjektID", width: "100px" },
    { field: "kostentraegernummer", header: "KTR", width: "120px" },
    { field: "projekttitel", header: "Projekttitel", width: "280px" },
    { field: "fullNameUppercase", header: "Mitarbeiter", width: "160px" },
    { field: "pointInTimeYear", header: "Jahr", width: "70px" },
    { field: "pointInTimeMonth", header: "Monat", width: "70px" },
    { field: "pointInTimeAsShortDateString", header: "Datum", width: "100px" },
    { field: "vonAsText", header: "Von", width: "80px", align: "end" },
    { field: "bisAsText", header: "Bis", width: "80px", align: "end" },
    { field: "dauer", header: "Dauer", width: "90px", align: "end" },
    { field: "bemerkung", header: "Bemerkung", width: "220px" },
    {
      field: "arbeitspaketBezeichnung",
      header: "Arbeitspaket",
      width: "200px",
    },
    { field: "isReisezeitAsText", header: "Reisezeit", width: "90px" },
    { field: "reisezeitArt", header: "Aktiv/Passiv", width: "100px" },
  ],
});

const buildBisMeldungView = (): ReportView => ({
  columns: [
    { field: "projektnummer", header: "Projektnummer", width: "130px" },
    { field: "projekttitelSubstring", header: "Projekttitel", width: "260px" },
    { field: "projektleiter", header: "Projektleiter", width: "160px" },
    { field: "department", header: "Institut/Abteilung", width: "180px" },
    {
      field: "studiengaengeAsText",
      header: "Studiengangszuweisung",
      width: "200px",
    },
    { field: "projektbeginn", header: "Projektbeginn", width: "120px" },
    { field: "projektendeAsText", header: "Projektende", width: "120px" },
    { field: "projektstatus", header: "Projektstatus", width: "140px" },
    {
      field: "errorStatus",
      header: "Prüfstatus",
      width: "220px",
      format: "html",
    },
    { field: "urlToProjectData", header: "", width: "80px", format: "link" },
  ],
});

const buildBisMeldungAlleFelderView = (): ReportView => ({
  columns: [
    { field: "projektnummer", header: "Projektnummer", width: "130px" },
    { field: "projekttitel", header: "Projekttitel", width: "260px" },
    { field: "projektbeginn", header: "Projektbeginn", width: "120px" },
    { field: "projektende", header: "Projektende", width: "120px" },
    {
      field: "forschungsschwerpunktCode",
      header: "Forschungsschwerpunkt Code",
      width: "180px",
    },
    { field: "projektstatus", header: "Projektstatus", width: "140px" },
    {
      field: "personalKosten",
      header: "Personalkosten",
      width: "130px",
      align: "end",
      format: "currency",
    },
    {
      field: "sonstigeInterneKosten",
      header: "Sonstige Interne Kosten",
      width: "170px",
      align: "end",
      format: "currency",
    },
    {
      field: "externeKosten",
      header: "Externe Kosten",
      width: "130px",
      align: "end",
      format: "currency",
    },
    {
      field: "kooperationsPartnerCode",
      header: "Kooperationspartner Code",
      width: "200px",
    },
    {
      field: "finanzierungsCode",
      header: "Finanzierungsart Code",
      width: "180px",
    },
    {
      field: "finanzierungsartBetrag",
      header: "Finanzierungsart Betrag",
      width: "180px",
      align: "end",
      format: "currency",
    },
    { field: "stgKz", header: "StgKz", width: "100px" },
  ],
});

const buildIststundenZeitaufzeichnungView = (): ReportView => ({
  columns: [
    { field: "monatJahr", header: "Monat", width: "120px" },
    {
      field: "sollStunden",
      header: "Sollstunden",
      width: "110px",
      align: "end",
      format: "number",
    },
    {
      field: "gesamtStunden",
      header: "Gesamtstunden",
      width: "120px",
      align: "end",
      format: "number",
    },
    {
      field: "istStunden",
      header: "Iststunden",
      width: "110px",
      align: "end",
      format: "number",
    },
    {
      field: "urlaub",
      header: "Urlaub",
      width: "100px",
      align: "end",
      format: "number",
    },
    {
      field: "krankenstand",
      header: "Krankenstand",
      width: "120px",
      align: "end",
      format: "number",
    },
    {
      field: "zeitausgleich",
      header: "Zeitausgleich",
      width: "120px",
      align: "end",
      format: "number",
    },
    {
      field: "pflegeurlaub",
      header: "Pflegeurlaub",
      width: "120px",
      align: "end",
      format: "number",
    },
    {
      field: "sonstigeNichtArbeitszeiten",
      header: "sonstige Nicht-Arbeitszeiten",
      width: "200px",
      align: "end",
      format: "number",
    },
  ],
});

export const REPORTS: ReportDefinition[] = [
  {
    key: "arbeitszeitauswertung",
    label: "Arbeitszeitauswertung Mitarbeiter:innen",
    requiresDates: true,
    requiresMitarbeiter: true,
    fetch: (s, range, mitarbeiterId) =>
      s.getAuswertungProjektmitarbeiterArbeitszeiten(
        mitarbeiterId as number,
        range.from,
        range.to,
      ),
    view: buildZeitaufzeichnungView,
  },
  {
    key: "berichte",
    label: "Berichte",
    requiresDates: false,
    fetch: (s) => s.getAuswertungBerichteProjekte(),
    view: buildBerichteView,
  },
  {
    key: "bisMeldung",
    label: "BIS-Meldung",
    requiresDates: false,
    requiresMeldeDatum: true,
    fetch: (s, _r, _m, meldeDatum) =>
      s.getAuswertungBisProjekte(meldeDatum as Date),
    view: buildBisMeldungView,
  },
  {
    key: "bisMeldungAlleFelder",
    label: "BIS-Meldung alle Felder",
    requiresDates: false,
    requiresMeldeDatum: true,
    fetch: (s, _r, _m, meldeDatum) =>
      s.getAuswertungBisProjekteAll(meldeDatum as Date),
    view: buildBisMeldungAlleFelderView,
  },
  {
    key: "meineProjektplanstunden",
    label: "Meine Projektplanstunden",
    requiresDates: false,
    fetch: (s) => s.getAuswertungRessourcenplanunguser(),
    view: buildMeineProjektplanstundenView,
  },
  {
    key: "offeneZahlungsliste",
    label: "Offene Zahlungsliste",
    requiresDates: false,
    fetch: (s) => s.getAuswertungOffeneRechnungen(),
    view: buildOffeneZahlungslisteView,
  },
  {
    key: "paralleleProjekte",
    label: "Parallele Projekte",
    requiresDates: true,
    fetch: (s, range) => s.getAuswertungParalleleProjekte(range.from, range.to),
    view: buildParalleleProjekteView,
  },
  {
    key: "projektleiterBmd",
    label: "Projektleiter aus BMD vs. Projekte",
    requiresDates: false,
    fetch: (s) => s.getAuswertungProjektleiterProjekte(),
    view: buildProjektleiterBmdView,
  },
  {
    key: "projektmitarbeiterFinden",
    label: "Projektmitarbeiter finden",
    requiresDates: false,
    requiresMitarbeiter: true,
    fetch: (s, _range, mitarbeiterId) =>
      s.getAuswertungMitarbeiterProjekte(mitarbeiterId as number),
    view: buildProjektmitarbeiterFindenView,
  },
  {
    key: "projektmitarbeiterMitProjekten",
    label: "ProjektmitarbeiterInnen mit Projekten",
    requiresDates: false,
    fetch: (s) => s.getAuswertungAlleMitarbeiterProjekte(),
    view: buildProjektmitarbeiterMitProjektenView,
  },
  {
    key: "projektpartner",
    label: "Projektpartner",
    requiresDates: false,
    fetch: (s) => s.getAuswertungProjektpartnerProjekte(),
    view: buildProjektpartnerView,
  },
  {
    key: "projektpartnerProProjekt",
    label: "Projektpartner pro Projekt",
    requiresDates: false,
    fetch: (s) => s.getAuswertungProjekteProjektpartner(),
    view: buildProjektpartnerProProjektView,
  },
  {
    key: "ressourcenplanung",
    label: "Ressourcenplanung",
    requiresDates: false,
    fetch: (s) => s.getAuswertungRessourcenplanung(),
    view: buildMeineProjektplanstundenView,
  },
  {
    key: "sollIstStundenProjekte",
    label: "Soll-Ist-Stunden Projekte",
    requiresDates: false,
    fetch: (s) => s.getAuswertungProjektmitarbeiterArbeitszeitenVergleich(),
    prepare: prepareSollIstStunden,
  },
  {
    key: "stundenreduktionen",
    label: "Stundenreduktionen",
    requiresDates: false,
    fetch: (s) => s.getAuswertungStundenreduktionItems(),
    prepare: prepareStundenreduktionen,
  },
  {
    key: "stundenreduktionenVergleich",
    label: "Stundenreduktionen Vergleich",
    requiresDates: false,
    fetch: (s) => s.getAuswertungStundenreduktionItemsFHelixVergleich(false),
    prepare: prepareStundenreduktionenVergleich,
  },
  {
    key: "stundenreduktionenVergleichAktuell",
    label: "Stundenreduktionen Vergleich (Aktuell)",
    requiresDates: false,
    fetch: (s) => s.getAuswertungStundenreduktionItemsFHelixVergleich(true),
    prepare: prepareStundenreduktionenVergleich,
  },
  {
    key: "zeitaufzeichnung",
    label: "Zeitaufzeichnung",
    requiresDates: true,
    requiresMitarbeiter: true,
    fetch: (s, range, mitarbeiterId) =>
      s.getAuswertungIststundenZeitaufzeichnung(
        mitarbeiterId as number,
        range.from,
        range.to,
      ),
    view: buildIststundenZeitaufzeichnungView,
  },
  {
    key: "zuStellendeRechnungen",
    label: "Zu stellende Rechnungen lt. Zahlungsplan",
    requiresDates: false,
    fetch: (s) => s.getAuswertungZuStellendeRechnungen(),
    view: buildZuStellendeRechnungenView,
  },
];
